import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EFeatureToggles, EProfileTabs, EStoreFeatures, EUserRoles } from '../../configurations/common';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { CustomerFacade } from '../../facades/customer.facade';
import { ICustomerExtended } from '../../models/customer.models';
import { IconType } from '../../models/settings.model';
import { CanComponentDeactivate } from '../../shared/interfaces/can-component-deactivate';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';

@Component({
  selector: 'app-page-my-profile',
  templateUrl: './page-my-profile.component.html',
  styleUrls: ['./page-my-profile.component.scss'],
  standalone: false,
})
export class PageMyProfileComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private customerFacade: CustomerFacade = inject(CustomerFacade);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private analyticsService: AnalyticsService = inject(AnalyticsService);

  customerData!: ICustomerExtended;
  customerId!: string;
  companyRoles!: EUserRoles[];
  businessUnitName!: string;
  businessUnitNumber!: string;
  currentTab: string = EProfileTabs.PERSONAL;
  profileTabs: typeof EProfileTabs = EProfileTabs;
  iconType: typeof IconType = IconType;
  canClose: boolean = true;
  isLoading: boolean = true;
  showModal: boolean = false;
  pageReadyEventFired: boolean = false;

  isPredefinedEquipmentFlowEnabled$: Observable<boolean> = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW);
  subject$: Subject<boolean> = new Subject<boolean>();
  private unsubscribe$: Subject<void> = new Subject<void>();
  private beforeUnloadSubscription!: Subscription;

  ngOnInit(): void {
    if (!this.configurationFacade.isFeatureAvailable(EStoreFeatures.MY_PROFILE)) {
      this.router.navigate([this.configurationFacade.getCurrentParam()]);
    }

    this.route.queryParams
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        this.currentTab = params.tab
          ? params.tab
          : this.currentTab;
      });

    this.selectCustomerData();

    this.beforeUnloadSubscription = fromEvent(window, 'beforeunload').subscribe((event: BeforeUnloadEvent) => {
      if (!this.canClose) {
        event.preventDefault();
        event.returnValue = ''; // Required for most browsers to show a confirmation dialog
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    // Unsubscribe from beforeunload event
    if (this.beforeUnloadSubscription) {
      this.beforeUnloadSubscription.unsubscribe();
      this.beforeUnloadSubscription = null!;
    }
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.canClose) {
      return true;
    }
    this.showModal = true;

    return this.subject$.asObservable();
  }

  discardChangesAndLeavePage(): void {
    this.showModal = false;
    this.subject$.next(true);
  }

  changeTab(tab: string): void {
    this.currentTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {tab: tab},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private selectCustomerData(): void {
    this.customerFacade.selectCustomerData().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe({
      next: customerData => {
        if (customerData) {
          if (customerData.customerData) {
            this.customerData = customerData.customerData;
          }
          this.customerId = customerData.customerId;
          this.companyRoles = customerData.companyRoles;
          this.businessUnitName = customerData.businessUnitName;
          this.businessUnitNumber = customerData.businessUnitNumber;
          this.isLoading = false;
          this.trackPageReady();
        }
      },
    });
  }

  trackPageReady(): void {
    if (!this.pageReadyEventFired && !this.isLoading) {
      this.analyticsService.trackPageReady('My Profile', PageTypes.MY_PROFILE);
      // Necessary as switching tabs causes customer data to be reloaded and we want the event
      // to be triggered only once in the component lifecycle
      this.pageReadyEventFired = true;
    }
  }
}
